import { useState, useRef, useEffect } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useT } from "@/i18n";
import { PageHeader, money, CARRIER_OPTIONS, DOC_TYPES, CarrierBadge } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Trash, Plus, UploadSimple, FileText, X, ArrowCounterClockwise, Buildings } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";

const emptyItem = () => ({ name: "", sku: "", quantity: 1, unit_price: 0 });

export default function PlaceOrder() {
  const navigate = useNavigate();
  const { id } = useParams();
  const editMode = !!id;
  const [params] = useSearchParams();
  const reorderId = params.get("reorder");
  const [reorderFrom, setReorderFrom] = useState("");
  const { t: tt } = useT();
  const { isTeam } = useAuth();
  const [clients, setClients] = useState([]);
  const [clientId, setClientId] = useState("");
  useEffect(() => { if (isTeam && !editMode) api.get("/clients").then((r) => setClients(r.data || [])).catch(() => {}); }, [isTeam, editMode]);
  const [items, setItems] = useState([emptyItem()]);
  const [supplyType, setSupplyType] = useState("regular");
  const [notes, setNotes] = useState("");
  const [requiredBy, setRequiredBy] = useState("");
  const [carrier, setCarrier] = useState("");
  const [patientName, setPatientName] = useState("");
  const [postalCode, setPostalCode] = useState("");
  const [address, setAddress] = useState("");
  const [destinationCountry, setDestinationCountry] = useState("");
  const [cpf, setCpf] = useState("");
  const [brokerDetails, setBrokerDetails] = useState("");
  const [clientReference, setClientReference] = useState("");
  const [busy, setBusy] = useState(false);
  const [docs, setDocs] = useState({ commercial_invoice: null, packing_list: null, patient_docs: null });
  const docRefs = useRef({});
  const setDocFile = (key, file) => setDocs((p) => ({ ...p, [key]: file || null }));

  const isBrazil = ["brazil", "brasil", "br"].includes(destinationCountry.trim().toLowerCase());
  const cpfDigits = cpf.replace(/\D/g, "");
  const cpfValid = (d) => {
    if (d.length !== 11 || /^(\d)\1{10}$/.test(d)) return false;
    for (const n of [9, 10]) {
      let s = 0;
      for (let i = 0; i < n; i++) s += Number(d[i]) * (n + 1 - i);
      if (((s * 10) % 11) % 10 !== Number(d[n])) return false;
    }
    return true;
  };
  const cpfOk = cpfValid(cpfDigits);
  const cepDigits = postalCode.replace(/\D/g, "");
  const [cepInfo, setCepInfo] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const lastCep = useRef("");
  const lookupCep = async (code) => {
    const d = code.replace(/\D/g, "");
    if (!isBrazil || d.length !== 8) { setCepInfo(""); return; }
    if (lastCep.current === d) return;
    lastCep.current = d;
    setCepInfo("…");
    try {
      const r = await fetch(`https://viacep.com.br/ws/${d}/json/`);
      const j = await r.json();
      if (j.erro) { setCepInfo("CEP not found"); return; }
      setCepInfo(`✓ ${j.localidade} — ${j.uf}`);
      setCity(j.localidade || ""); setState(j.uf || "");
      setPostalCode(`${d.slice(0, 5)}-${d.slice(5)}`);
      if (!address.trim() && j.logradouro) setAddress(`${j.logradouro}, ${j.bairro}`);
    } catch { setCepInfo("lookup failed"); }
  };
  const onCepChange = (v) => { setPostalCode(v); if (isBrazil && v.replace(/\D/g, "").length === 8) lookupCep(v); };

  useEffect(() => {
    const src = editMode ? id : reorderId;
    if (!src) return;
    api.get(`/orders/${src}`).then(({ data: o }) => {
      setItems((o.items || []).map((it) => ({ name: it.name || "", sku: it.sku || "", quantity: it.quantity || 1, unit_price: it.unit_price || 0 })));
      setSupplyType(o.supply_type || "regular");
      setNotes(o.notes || "");
      setRequiredBy(o.required_by || "");
      setCarrier(o.preferred_carrier || "");
      setPatientName(o.patient_name || "");
      setPostalCode(o.postal_code || "");
      setAddress(o.address || "");
      setDestinationCountry(o.destination_country || "");
      setCpf(o.cpf || "");
      setCity(o.city || ""); setState(o.state || "");
      setBrokerDetails(o.broker_details || "");
      if (o.client_id) setClientId(o.client_id);
      if (!editMode) { setReorderFrom(o.order_number); setRequiredBy(""); setClientReference(""); return; }
      setClientReference(o.client_reference || "");
      if (o.status !== "pending") { toast.error("This order can no longer be edited"); navigate(`/orders/${id}`); }
    }).catch(() => { toast.error("Could not load order"); navigate("/orders"); });
  }, [editMode, id, reorderId]); // eslint-disable-line react-hooks/exhaustive-deps

  const update = (i, field, val) => {
    setItems((prev) => prev.map((it, idx) => (idx === i ? { ...it, [field]: val } : it)));
  };
  const addItem = () => setItems((p) => [...p, emptyItem()]);
  const removeItem = (i) => setItems((p) => p.filter((_, idx) => idx !== i));

  const total = items.reduce((s, it) => s + (Number(it.quantity) || 0) * (Number(it.unit_price) || 0), 0);

  const submit = async () => {
    const valid = items.filter((it) => it.name.trim());
    if (valid.length === 0) return toast.error("Add at least one product");
    if (isTeam && !editMode && !clientId) return toast.error("Select the client this order is for");
    if (isBrazil) {
      if (cepDigits.length !== 8) return toast.error("Brazil orders need an 8-digit CEP (postal code) — e.g. 01310-100");
      if (!cpfOk) return toast.error("Brazil orders need a valid 11-digit CPF for the consignee");
      if (!address.trim()) return toast.error("Brazil orders need the delivery address");
    }
    setBusy(true);
    try {
      const payload = {
        items: valid.map((it) => ({
          name: it.name,
          sku: it.sku,
          quantity: Number(it.quantity) || 1,
          unit_price: Number(it.unit_price) || 0,
        })),
        notes,
        supply_type: supplyType,
        required_by: requiredBy || null,
        preferred_carrier: carrier || null,
        patient_name: patientName || null,
        postal_code: postalCode || null,
        address: address || null,
        city: city || null,
        state: state || null,
        destination_country: destinationCountry || null,
        cpf: isBrazil ? cpfDigits.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4") : null,
        broker_details: brokerDetails || null,
        client_reference: clientReference || null,
        ...(isTeam && !editMode ? { client_id: clientId } : {}),
      };
      if (editMode) {
        const { data } = await api.patch(`/orders/${id}/client-edit`, payload);
        toast.success(`Order ${data.order_number} updated`);
        navigate(`/orders/${id}`);
        return;
      }
      const { data } = await api.post("/orders", payload);
      const pending = DOC_TYPES.filter((t) => docs[t.key]);
      if (pending.length) {
        for (const t of pending) {
          const fd = new FormData();
          fd.append("file", docs[t.key]);
          fd.append("label", t.label);
          fd.append("doc_type", t.key);
          await api.post(`/orders/${data.id}/documents`, fd, { headers: { "Content-Type": "multipart/form-data" } }).catch(() => toast.error(`${t.label} upload failed`));
        }
      }
      toast.success(`Order ${data.order_number} placed`);
      navigate(`/orders/${data.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to place order");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6 max-w-4xl">
      <PageHeader subtitle={isTeam && !editMode ? "On behalf of a client — follows the standard order workflow" : tt("place.subtitle")} title={editMode ? "Edit Order" : isTeam ? "New Order" : tt("place.title")} />
      {isTeam && !editMode && (
        <div className="hp-card p-5" data-testid="team-client-picker">
          <p className="text-xs uppercase tracking-[0.2em] font-semibold text-muted-foreground mb-3">Client</p>
          <div className="grid sm:grid-cols-[1fr_auto] gap-3 items-end">
            <div className="space-y-1.5">
              <Label>Place this order for</Label>
              <Select value={clientId} onValueChange={setClientId}>
                <SelectTrigger data-testid="team-client-select"><SelectValue placeholder="Select a client…" /></SelectTrigger>
                <SelectContent>
                  {clients.map((c) => <SelectItem key={c.id} value={c.id} data-testid={`team-client-option-${c.id}`}>{c.name}{c.country ? ` · ${c.country}` : ""}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2 text-xs text-muted-foreground pb-2.5"><Buildings size={16} className="text-primary" /> The order appears in the client's portal and follows approval → procurement → dispatch as usual.</div>
          </div>
        </div>
      )}
      {reorderFrom && (
        <div className="flex items-center gap-2 rounded-md border border-primary/30 bg-primary/5 px-4 py-3 text-sm" data-testid="reorder-banner">
          <ArrowCounterClockwise size={16} className="text-primary shrink-0" />
          <span>Reordering from <span className="font-semibold">{reorderFrom}</span> — items, patient and delivery details are pre-filled. Review, adjust quantities if needed and submit as a new order.</span>
        </div>
      )}

      <div className="hp-card p-5">
        <p className="text-xs uppercase tracking-[0.2em] font-semibold text-muted-foreground mb-3">{tt("place.supplyTitle")}</p>
        <div className="grid sm:grid-cols-2 gap-3" data-testid="supply-type-toggle">
          {[
            { key: "regular", label: tt("place.regular"), desc: tt("place.regularDesc") },
            { key: "bulk", label: tt("place.bulk"), desc: tt("place.bulkDesc") },
          ].map((o) => (
            <button
              key={o.key}
              type="button"
              onClick={() => setSupplyType(o.key)}
              data-testid={`supply-type-${o.key}`}
              className={`text-left rounded-md border p-4 transition-colors ${supplyType === o.key ? "border-primary bg-primary/5 ring-1 ring-primary" : "border-border hover:border-primary/40"}`}
            >
              <div className="flex items-center gap-2">
                <span className={`h-3.5 w-3.5 rounded-full border-2 ${supplyType === o.key ? "border-primary bg-primary" : "border-muted-foreground"}`} />
                <span className="font-semibold text-sm">{o.label}</span>
              </div>
              <p className="text-xs text-muted-foreground mt-1.5">{o.desc}</p>
            </button>
          ))}
        </div>
      </div>

      <div className="hp-card overflow-hidden">
        <div className="hidden sm:grid grid-cols-12 gap-3 px-4 py-3 bg-muted/50 text-xs uppercase tracking-wide font-semibold text-muted-foreground">
          <div className="col-span-5">{tt("place.product")}</div>
          <div className="col-span-2">{tt("place.sku")}</div>
          <div className="col-span-2">{tt("place.qty")}</div>
          <div className="col-span-2">{tt("place.unitPrice")}</div>
          <div className="col-span-1" />
        </div>
        <div className="divide-y divide-border">
          {items.map((it, i) => (
            <div key={i} className="grid grid-cols-6 sm:grid-cols-12 gap-3 px-4 py-3 items-center" data-testid={`order-item-row-${i}`}>
              <Input
                className="col-span-5 rounded-sm"
                placeholder="Paracetamol 500mg"
                value={it.name}
                onChange={(e) => update(i, "name", e.target.value)}
                data-testid={`item-name-${i}`}
              />
              <button
                className="col-span-1 sm:hidden flex justify-center text-muted-foreground hover:text-destructive transition-colors min-h-[42px]"
                onClick={() => removeItem(i)}
                disabled={items.length === 1}
                aria-label="Remove line"
              >
                <Trash size={18} />
              </button>
              <Input
                className="col-span-2 rounded-sm"
                placeholder="SKU"
                value={it.sku}
                onChange={(e) => update(i, "sku", e.target.value)}
                data-testid={`item-sku-${i}`}
              />
              <Input
                type="number"
                min="1"
                className="col-span-2 rounded-sm"
                placeholder="Qty"
                value={it.quantity}
                onChange={(e) => update(i, "quantity", e.target.value)}
                data-testid={`item-qty-${i}`}
              />
              <Input
                type="number"
                min="0"
                step="0.01"
                className="col-span-2 rounded-sm"
                placeholder="Unit price"
                value={it.unit_price}
                onChange={(e) => update(i, "unit_price", e.target.value)}
                data-testid={`item-price-${i}`}
              />
              <button
                className="col-span-1 hidden sm:flex justify-center text-muted-foreground hover:text-destructive transition-colors"
                onClick={() => removeItem(i)}
                disabled={items.length === 1}
                data-testid={`remove-item-${i}`}
              >
                <Trash size={18} />
              </button>
            </div>
          ))}
        </div>
        <div className="px-4 py-3 border-t border-border">
          <Button variant="outline" size="sm" className="rounded-sm" onClick={addItem} data-testid="add-item-button">
            <Plus size={16} className="mr-2" /> {tt("place.addLine")}
          </Button>
        </div>
      </div>

      <div className="grid sm:grid-cols-3 gap-6">
        <div>
          <Label className="text-xs uppercase tracking-wide">{tt("place.shipMethod")}</Label>
          <Select value={carrier} onValueChange={setCarrier}>
            <SelectTrigger className="mt-1.5 rounded-sm" data-testid="preferred-carrier"><SelectValue placeholder={tt("place.selectCarrier")} /></SelectTrigger>
            <SelectContent>
              {CARRIER_OPTIONS.map((c) => <SelectItem key={c.code} value={c.code}><CarrierBadge carrier={c.code} /></SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wide">{tt("place.requiredBy")}</Label>
          <Input type="date" className="mt-1.5 rounded-sm" value={requiredBy} onChange={(e) => setRequiredBy(e.target.value)} data-testid="required-by" />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wide">{tt("place.reference")}</Label>
          <Input className="mt-1.5 rounded-sm" value={clientReference} onChange={(e) => setClientReference(e.target.value)} placeholder={tt("place.referencePh")} data-testid="order-client-reference" />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wide">{tt("place.notes")}</Label>
          <Textarea className="mt-1.5 rounded-sm" rows={1} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder={tt("place.notesPh")} data-testid="order-notes" />
        </div>
      </div>

      <div className="hp-card p-6 space-y-4">
        <p className="text-xs uppercase tracking-[0.2em] font-semibold text-muted-foreground">{tt("place.destTitle")}</p>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label className="text-xs uppercase tracking-wide">{tt("place.patientName")}</Label>
            <Input className="mt-1.5 rounded-sm" value={patientName} onChange={(e) => setPatientName(e.target.value)} placeholder={tt("place.fullName")} data-testid="order-patient-name" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wide">{tt("place.destCountry")}</Label>
            <Input className="mt-1.5 rounded-sm" value={destinationCountry} onChange={(e) => setDestinationCountry(e.target.value)} placeholder="e.g. Brazil" data-testid="order-destination-country" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wide">{tt("place.postal")}{isBrazil && <span className="text-red-600 ml-1">*</span>}{cepInfo && <span className={`ml-2 normal-case tracking-normal font-normal ${cepInfo.includes("not") ? "text-amber-700" : "text-green-700"}`} data-testid="order-cep-info">{cepInfo}</span>}</Label>
            <Input className="mt-1.5 rounded-sm" value={postalCode} onChange={(e) => onCepChange(e.target.value)} onBlur={(e) => lookupCep(e.target.value)} placeholder={isBrazil ? "CEP — 00000-000 (fills city & state)" : "Postal / ZIP code"} inputMode={isBrazil ? "numeric" : undefined} required={isBrazil} data-testid="order-postal-code" />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-2">
              <Label className="text-xs uppercase tracking-wide">{tt("place.city")}</Label>
              <Input className="mt-1.5 rounded-sm" value={city} onChange={(e) => setCity(e.target.value)} placeholder={isBrazil ? "Auto from CEP" : "City"} data-testid="order-city" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wide">{tt("place.state")}</Label>
              <Input className="mt-1.5 rounded-sm uppercase" value={state} onChange={(e) => setState(e.target.value.toUpperCase())} placeholder={isBrazil ? "UF" : "State"} maxLength={isBrazil ? 2 : 40} data-testid="order-state" />
            </div>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wide">{tt("place.address")}{isBrazil && <span className="text-red-600 ml-1">*</span>}</Label>
            <Input className="mt-1.5 rounded-sm" value={address} onChange={(e) => setAddress(e.target.value)} placeholder={tt("place.addressPh")} data-testid="order-address" />
          </div>
          {isBrazil && (
            <div data-testid="order-cpf-wrap">
              <Label className="text-xs uppercase tracking-wide">{tt("place.cpf")}<span className="text-red-600 ml-1">*</span>{cpfDigits.length > 0 && <span className={`ml-2 normal-case tracking-normal font-normal ${cpfOk ? "text-green-700" : "text-red-600"}`} data-testid="order-cpf-status">{cpfOk ? "✓ valid" : cpfDigits.length === 11 ? "✗ invalid CPF" : `${cpfDigits.length}/11 digits`}</span>}</Label>
              <Input className="mt-1.5 rounded-sm font-mono" value={cpf} onChange={(e) => setCpf(e.target.value)} placeholder="000.000.000-00" inputMode="numeric" required data-testid="order-cpf" />
              <p className="text-xs text-muted-foreground mt-1">Required by Brazilian customs — used automatically on the FedEx AWB.</p>
            </div>
          )}
          <div className="sm:col-span-2">
            <Label className="text-xs uppercase tracking-wide">{tt("place.broker")}</Label>
            <Textarea className="mt-1.5 rounded-sm" rows={2} value={brokerDetails} onChange={(e) => setBrokerDetails(e.target.value)} placeholder={tt("place.brokerPh")} data-testid="order-broker-details" />
          </div>
        </div>
      </div>

      <div className="hp-card p-6 space-y-4">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] font-semibold text-muted-foreground">{tt("place.docsTitle")}</p>
          <p className="text-sm text-muted-foreground mt-1">{tt("place.docsHint")}</p>
        </div>
        <div className="grid sm:grid-cols-3 gap-4">
          {DOC_TYPES.map((t) => (
            <div key={t.key} className="border border-border rounded-md p-4 flex flex-col gap-3" data-testid={`doc-slot-${t.key}`}>
              <div className="flex items-center gap-2 text-sm font-medium">
                <FileText size={16} className="text-primary" /> {t.label}
              </div>
              {docs[t.key] ? (
                <div className="flex items-center justify-between gap-2 rounded-sm bg-muted/50 px-3 py-2">
                  <span className="text-xs truncate" data-testid={`doc-name-${t.key}`}>{docs[t.key].name}</span>
                  <button type="button" className="text-muted-foreground hover:text-destructive shrink-0" onClick={() => { setDocFile(t.key, null); if (docRefs.current[t.key]) docRefs.current[t.key].value = ""; }} data-testid={`doc-remove-${t.key}`}>
                    <X size={14} />
                  </button>
                </div>
              ) : (
                <Button type="button" variant="outline" size="sm" className="rounded-sm" onClick={() => docRefs.current[t.key]?.click()} data-testid={`doc-choose-${t.key}`}>
                  <UploadSimple size={14} className="mr-1.5" /> {tt("place.chooseFile")}
                </Button>
              )}
              <input
                ref={(el) => (docRefs.current[t.key] = el)}
                type="file"
                className="hidden"
                onChange={(e) => setDocFile(t.key, e.target.files?.[0])}
                data-testid={`doc-input-${t.key}`}
              />
            </div>
          ))}
        </div>
      </div>

      <div className="flex items-center justify-between border-t border-border pt-5">
        <div>
          <p className="text-xs uppercase tracking-wide text-muted-foreground">{tt("place.estTotal")}</p>
          <p className="font-heading text-3xl font-extrabold tracking-tight" data-testid="order-total">{money(total)}</p>
        </div>
        <Button className="rounded-sm h-11 px-8" onClick={submit} disabled={busy} data-testid="submit-order">
          {busy ? tt("place.placing") : (editMode ? "Save changes" : tt("place.submit"))}
        </Button>
      </div>
    </div>
  );
}
